import { Metadata } from "next";
import { clsx, type ClassValue } from "clsx";
import ms from "ms";
import { twMerge } from "tailwind-merge";

import { siteConfig } from "@/config/site";
import { getAppUrl } from "@/lib/urls";

import yahooFinance from 'yahoo-finance2';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function constructMetadata({
  title = siteConfig.name,
  description = siteConfig.description,
  image = siteConfig.ogImage,
  icons = "/favicon.ico",
  noIndex = false,
}: {
  title?: string;
  description?: string;
  image?: string;
  icons?: string;
  noIndex?: boolean;
} = {}): Metadata {
  return {
    title,
    description,
    keywords: [
      "Options",
      "Cash Secured Puts",
      "Options Trading",
      "Options screener",
      "Options automatization",
      "Options trading alerts",
      "Options trading community",
      "Passive income through selling options",
      "The wheel strategy",
      "Options Wheel",
      "Options quant analysis"
    ],
    authors: [
      {
        name: "ivanoreh",
      },
    ],
    creator: "ivanoreh",
    openGraph: {
      type: "website",
      locale: "en_US",
      url: siteConfig.url,
      title,
      description,
      siteName: title,
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [image],
      creator: "@quantwheel",
    },
    icons,
    metadataBase: new URL(siteConfig.url),
    manifest: `${siteConfig.url}/site.webmanifest`,
    ...(noIndex && {
      robots: {
        index: false,
        follow: false,
      },
    }),
  };
}

export function formatDateTimeTimezone(input: string | number): string {
  const date = new Date(input);
  return date.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}
export function formatDate(input: string | number): string {
  const date = new Date(input);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function absoluteUrl(path: string) {
  return `${getAppUrl()}${path}`;
}

// Utils from precedent.dev
export const timeAgo = (timestamp: Date, timeOnly?: boolean): string => {
  if (!timestamp) return "never";
  return `${ms(Date.now() - new Date(timestamp).getTime())}${
    timeOnly ? "" : " ago"
  }`;
};

export async function fetcher<JSON = any>(
  input: RequestInfo,
  init?: RequestInit,
): Promise<JSON> {
  const res = await fetch(input, init);

  if (!res.ok) {
    const json = await res.json();
    if (json.error) {
      const error = new Error(json.error) as Error & {
        status: number;
      };
      error.status = res.status;
      throw error;
    } else {
      throw new Error("An unexpected error occurred");
    }
  }

  return res.json();
}

export function nFormatter(num: number, digits?: number) {
  if (!num) return "0";
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "K" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "G" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    });
  return item
    ? (num / item.value).toFixed(digits || 1).replace(rx, "$1") + item.symbol
    : "0";
}

export function capitalize(str: string) {
  if (!str || typeof str !== "string") return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const truncate = (str: string, length: number) => {
  if (!str || str.length <= length) return str;
  return `${str.slice(0, length)}...`;
};

// Server-side helper to read anonymous ID cookie, if present
export function getAnonymousIdFromCookies(): string | undefined {
  try {
    // Dynamically import to avoid bundling issues in edge/client
    const { cookies } = require('next/headers');
    const { ANON_ID_COOKIE_NAME } = require('./constants');
    const c = cookies()?.get?.(ANON_ID_COOKIE_NAME);
    return c?.value;
  } catch {
    return undefined;
  }
}

// Server-side helper to read GA4 client_id from _ga cookie
// GA4 cookie format: GA{version}.{container}.{timestamp}.{random}
// The client_id is the last two parts: {timestamp}.{random}
export function getGaClientIdFromCookies(): string | undefined {
  try {
    const { cookies } = require('next/headers');
    const gaCookie = cookies()?.get?.('_ga');
    if (!gaCookie?.value) return undefined;
    
    // Match GA followed by two dot-separated numbers, then capture the rest
    const match = gaCookie.value.match(/^GA\d+\.\d+\.(.+)$/);
    return match ? match[1] : undefined;
  } catch {
    return undefined;
  }
}

export const getBlurDataURL = async (url: string | null) => {
  if (!url) {
    return "data:image/webp;base64,AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=";
  }

  if (url.startsWith("/_static/")) {
    url = `${siteConfig.url}${url}`;
  }

  try {
    const response = await fetch(
      `https://wsrv.nl/?url=${url}&w=50&h=50&blur=5`,
    );
    const buffer = await response.arrayBuffer();
    const base64 = Buffer.from(buffer).toString("base64");

    return `data:image/png;base64,${base64}`;
  } catch (error) {
    return "data:image/webp;base64,AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=";
  }
};

export const placeholderBlurhash =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAoJJREFUWEfFl4lu4zAMRO3cx/9/au6reMaOdkxTTl0grQFCRoqaT+SQotq2bV9N8rRt28xms87m83l553eZ/9vr9Wpkz+ezkT0ej+6dv1X81AFw7M4FBACPVn2c1Z3zLgDeJwHgeLFYdAARYioAEAKJEG2WAjl3gCwNYymQQ9b7/V4spmIAwO6Wy2VnAMikBWlDURBELf8CuN1uHQSrPwMAHK5WqwFELQ01AIXdAa7XawfAb3p6AOwK5+v1ugAoEq4FRSFLgavfQ49jAGQpAE5wjgGCeRrGdBArwHOPcwFcLpcGU1X0IsBuN5tNgYhaiFFwHTiAwq8I+O5xfj6fOz38K+X/fYAdb7fbAgFAjIJ6Aav3AYlQ6nfnDoDz0+lUxNiLALvf7XaDNGQ6GANQBKR85V27B4D3QQRw7hGIYlQKWGM79hSweyCUe1blXhEAogfABwHAXAcqSYkxCtHLUK3XBajSc4Dj8dilAeiSAgD2+30BAEKV4GKcAuDqB4TdYwBgPQByCgApUBoE4EJUGvxUjF3Q69/zLw3g/HA45ABKgdIQu+JPIyDnisCfAxAFNFM0EFNQ64gfS0EUoQP8ighrZSjn3oziZEQpauyKbfjbZchHUL/3AS/Dd30gAkxuRACgfO+EWQW8qwI1o+wseNuKcQiESjALvwNoMI0TcRzD4lFcPYwIM+JTF5x6HOs8yI7jeB5oKhpMRFH9UwaSCDB2Jmg4rc6E2TT0biIaG0rQhNqyhpHBcayTTSXH6vcDL7/sdqRK8LkwTsU499E8vRcAojHcZ4AxABdilgrp4lsXk8oVqgwh7+6H3phqd8J0Kk4vbx/+sZqCD/vNLya/5dT9fAH8g1WdNGgwbQAAAABJRU5ErkJggg==";

const POLYGON_API_KEY = process.env.POLYGON_API_TOKEN || '';

interface CompanyProfile {
  symbol: string;
  website: string;
}

async function getCompanyProfile(ticker: string): Promise<CompanyProfile | null> {
  // Try Polygon ticker details first (has homepage_url)
  if (POLYGON_API_KEY) {
    try {
      const res = await fetch(
        `https://api.massive.com/v3/reference/tickers/${ticker}?apiKey=${POLYGON_API_KEY}`,
        { signal: AbortSignal.timeout(5000) }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'OK' && data.results?.homepage_url) {
          return { symbol: ticker, website: data.results.homepage_url };
        }
      }
    } catch (error) {
      console.warn(`Polygon ticker details error for ${ticker}:`, error);
    }
  }

  // Fallback to Yahoo Finance
  try {
    const queryOptions = { modules: ['assetProfile'] };
    const result = await yahooFinance.quoteSummary(ticker, queryOptions);
    const assetProfile = result.assetProfile;
    if (assetProfile && assetProfile.website) {
      return { symbol: ticker, website: assetProfile.website };
    } else {
      console.warn(`No website data found for ${ticker}`);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching company profile for ${ticker}:`, error);
    return null;
  }
}

function cleanDomain(url) {
  // The regex:
  // ^https?:\/\/  --> matches "http://" or "https://" at the beginning
  // (www\.)?     --> optionally matches "www."
  return url.replace(/^https?:\/\/(www\.)?/i, '');
}

/**
 * Builds a mapping from ticker symbols to their websites. TODO : cache this
 * @param tickers - Array of ticker symbols.
 * @returns A Promise resolving to an object where keys are ticker symbols and values are websites.
 */
export async function getTickerLogos(tickers: string[]): Promise<{ [ticker: string]: string }> {
  const mapping: { [ticker: string]: string } = {};

  // Process tickers in parallel
  const profiles = await Promise.all(tickers.map(ticker => getCompanyProfile(ticker)));
  tickers.forEach((ticker, index) => {
    const profile = profiles[index];
    if (profile && profile.website) {

      mapping[ticker] = `https://cdn.brandfetch.io/${cleanDomain(profile.website)}/w/50/h/50?c=1id4D7MVr1d-n3wTk-t`;
    }
  });
  return mapping;
}

/**
 * Check if US stock markets are currently open
 * Regular market hours are 9:30 AM - 4:00 PM Eastern Time, Monday through Friday
 * Markets are closed on US holidays (not implemented here for simplicity)
 */
export function areMarketsOpen(): boolean {
  const now = new Date();
  const day = now.getDay();
  
  // Check if it's a weekend (0 = Sunday, 6 = Saturday)
  if (day === 0 || day === 6) {
    return false;
  }
  
  // Convert to Eastern Time
  const easternTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const hours = easternTime.getHours();
  const minutes = easternTime.getMinutes();
  
  // Convert to minutes since midnight for easier comparison
  const currentMinutes = hours * 60 + minutes;
  
  // Market opens at 9:30 AM ET (570 minutes) and closes at 4:00 PM ET (960 minutes)
  return currentMinutes >= 570 && currentMinutes < 960;
}  